import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { FileKindEnum } from 'scribe-web-shared/enums/file-kind-enums';

import knowledgePageService from '../services/knowledgePageService';
import { generateRandomGradientColors } from '../v2/components/_page/generateRandomColors';
import { removeApplicationTag } from './actions/documentMetadataActions';

/**
 * @typedef {Object} UserDocumentPermissionResult
 * @property {string} email
 * @property {number} document_metadata_id
 * @property {string} permission_level
 */

/**
 * @typedef {Object} ModifyUserDocumentPermissionArgs
 * @property {string} id - The ID of the document
 * @property {string} userId - The ID of the user
 * @property {string} permissionLevel - The permission level
 */

/**
 * @type {function(ModifyUserDocumentPermissionArgs): Promise<UserDocumentPermissionResult>}
 */
export const addUserFilePermission = createAsyncThunk('knowledgePage/addUserFilePermission', async ({ id, userId, permissionLevel }) => {
  const user = await knowledgePageService.changeUserPermission(id, userId, permissionLevel);

  return user;
});

/**
 * @typedef {Object} ModifyCandidateDocumentPermissionArgs
 * @property {string} id - The ID of the document
 * @property {string} email - The email of the user
 * @property {string} permissionLevel - The permission level
 */

/**
 * @typedef {Object} CandidateDocumentPermissionResult
 * @property {string} email
 * @property {string} permission_level
 */

/**
 * @type {function(ModifyCandidateDocumentPermissionArgs): Promise<CandidateDocumentPermissionResult>}
 */
export const addCandidateFilePermission = createAsyncThunk(
  'knowledgePage/addCandidateFilePermission',
  async ({ id, email, permissionLevel }) => {
    const user = await knowledgePageService.changeCandidatePermission(id, email, permissionLevel);

    return user;
  }
);

/**
 * @type {function(string): Promise<Array<UserDocumentPermissionResult>>}
 */
export const getUsersWithPermission = createAsyncThunk('knowledgePage/getUsersWithPermission', async (id) => {
  const users = await knowledgePageService.getUsersWithPermission(id);

  return users;
});
/**
 * @type {function(string): Promise<Array<UserDocumentPermissionResult>>}
 */
export const getCandidatesWithPermission = createAsyncThunk('knowledgePage/getCandidatesWithPermissions', async (id) => {
  const users = await knowledgePageService.getCandidatesWithPermission(id);

  return users;
});

/**
 * @typedef {Object} FetchKnowledgePageInBackgroundProps
 * @property {string} id
 * @property {boolean} isAuthenticated
 */
/**
 * @type {function(FetchKnowledgePageInBackgroundProps): Promise<import('scribe-web-shared/types/document').WorkspaceDocument>}
 */
export const fetchKnowledgePageInBackground = createAsyncThunk('knowledgePage/fetchInBackground', async ({ id, isAuthenticated } = {}) => {
  return knowledgePageService.fetchKnowledgePage(id, isAuthenticated);
});

export const updateKnowledgePage = createAsyncThunk('knowledgePage/update', async (data, { getState, rejectWithValue }) => {
  const state = getState();
  const { documentId, bodyParams } = data;

  bodyParams.data_hash = state.knowledgePage?.currentKnowledgePage?.data_hash;
  try {
    const updatedFile = await knowledgePageService.updateKnowledgePage(documentId, bodyParams);

    return updatedFile;
  } catch (err) {
    return rejectWithValue(err.status);
  }
});

export const updateKnowledgePageMetadata = createAsyncThunk('knowledgePage/updateMetadata', async (data) => {
  const { documentId, bodyParams } = data;
  const updatedFile = await knowledgePageService.updateKnowledgePageMetadata(documentId, bodyParams);

  return updatedFile;
});

export const createKnowledgePage = createAsyncThunk(
  'knowledgePage/create',
  async ({ parentFolderId, documentId, documentTitle, editorJsData, location, selectedTaskId = '' }) => {
    const headerColors = generateRandomGradientColors();

    return knowledgePageService.createKnowledgePage(
      {
        folder_id: parentFolderId,
        document_id: documentId,
        title: documentTitle,
        editor_js_data: editorJsData,
        colorful_header: headerColors,
        allow_feedback: true,
        task_id: selectedTaskId
      },
      location
    );
  }
);

export const addDocumentToKnowledgePage = createAsyncThunk('knowledgePage/create', async ({ pageId, documentId }) => {
  return knowledgePageService.appendToKnowledgePage({
    data: { document_id: documentId },
    id: pageId
  });
});

export const setKnowledgePageAction = createAction('knowledgePage/setCurrentKnowledgePage', (page) => {
  return {
    payload: page
  };
});

export const defaultPageContent = `
        <p></p>
        <ScribeContainer showPlaceholder="true"></ScribeContainer>
        <p></p>`;

// if you change defaultPageContent, you MUST change defaultPageContentJson
export const defaultPageContentJson = `[{"type":"paragraph","attrs":{"textAlign":"left"}},{"type":"IncludeScribeExtension","attrs":{"showPlaceholder":true,"scribe":null,"scribeUrl":null,"scribeCardSize":"medium"}},{"type":"paragraph","attrs":{"textAlign":"left"}}]`;

export const defaultPageContentWithAI = `
        <p></p>
        <GettingStartedExtension></GettingStartedExtension>
        <p></p>`;

// if you change defaultPageContent, you MUST change defaultPageContentJson
export const defaultPageContentJsonWithAI = `[{"type":"paragraph","attrs":{"textAlign":"left"}},{"type":"GettingStartedExtension","attrs":{}},{"type":"paragraph","attrs":{"textAlign":"left"}}]`;

export const defaultPageContentGenerateAI = `
        <p></p>
        <ConnectedArtificialIntelligenceBar></ConnectedArtificialIntelligenceBar>
        <p></p>`;

export const defaultPageContentGenerateJson = `[{"type":"paragraph","attrs":{"textAlign":"left"}},{"type":"ConnectedArtificialIntelligenceBar","attrs":{}},{"type":"paragraph","attrs":{"textAlign":"left"}}]`;

export const INITIAL_STATE = {
  currentKnowledgePage: null,
  isLoading: false,
  isError: false,
  isEditMode: false,
  isUpdating: false,
  errorMessage: null,
  documentViewerId: undefined,
  showingLoadingModal: false,
  disableWindowFocus: false,
  candidates: [],
  usersWithAccess: []
};

const knowledgePageSlice = createSlice({
  name: 'knowledgePage',
  initialState: { ...INITIAL_STATE },
  reducers: {
    setCurrentKnowledgePage: (state, action) => {
      if (!action.payload.editor_js_data) {
        state.currentKnowledgePage = { ...action.payload, editor_js_data: defaultPageContentWithAI };
      } else {
        state.currentKnowledgePage = action.payload;
      }
      state.documentViewerId = undefined;
      state.showingLoadingModal = false;
    },
    setDocumentViewerId: (state, action) => {
      state.documentViewerId = action.payload;
    },
    disableWindowFocus: (state, action) => {
      state.disableWindowFocus = action.payload;
    },
    setLoadingModalActive: (state, action) => {
      state.showingLoadingModal = true;
    },
    setLoadingModalDisabled: (state, action) => {
      state.showingLoadingModal = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKnowledgePageInBackground.pending, (state, action) => {})
      .addCase(fetchKnowledgePageInBackground.fulfilled, (state, action) => {
        state.currentKnowledgePage = action.payload;
      })
      .addCase(fetchKnowledgePageInBackground.rejected, (state, action) => {})
      .addCase(updateKnowledgePage.fulfilled, (state, action) => {
        state.currentKnowledgePage = action.payload;
        state.isUpdating = false;
        state.showingLoadingModal = false;
      })
      .addCase(updateKnowledgePage.pending, (state) => {
        state.isUpdating = true;
        state.errorMessage = null;
      })
      .addCase(updateKnowledgePage.rejected, (state, action) => {
        state.data = null;
        state.isUpdating = false;
        state.errorMessage = action.payload;
        state.showingLoadingModal = false;
      })
      .addCase(updateKnowledgePageMetadata.fulfilled, (state, action) => {
        state.currentKnowledgePage = action.payload;
        state.isUpdating = false;
      })
      .addCase(updateKnowledgePageMetadata.pending, (state) => {
        state.isUpdating = true;
        state.errorMessage = null;
      })
      .addCase(updateKnowledgePageMetadata.rejected, (state, action) => {
        state.data = null;
        state.isUpdating = false;
        state.errorMessage = action.payload;
      })
      .addCase(getUsersWithPermission.fulfilled, (state, action) => {
        const usersWithPermission =
          action.payload?.map((user) => ({ ...user, permission_level: user.permission_level === 200 ? 'write' : 'read' })) || [];

        state.usersWithAccess = usersWithPermission;
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getUsersWithPermission.pending, (state) => {
        state.isLoadingUsersWithAccess = true;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getUsersWithPermission.rejected, (state, action) => {
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = true;
        state.errorMessage = action.payload;
      })
      .addCase(getCandidatesWithPermission.fulfilled, (state, action) => {
        state.candidates = action.payload;
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getCandidatesWithPermission.pending, (state) => {
        state.isLoadingUsersWithAccess = true;
        state.loadingUsersWithAccessError = false;
        state.errorMessage = null;
      })
      .addCase(getCandidatesWithPermission.rejected, (state, action) => {
        state.isLoadingUsersWithAccess = false;
        state.loadingUsersWithAccessError = true;
        state.errorMessage = action.payload;
      })
      .addCase(addUserFilePermission.fulfilled, (state, action) => {
        const { permissionLevel, userId } = action.meta.arg;

        if (permissionLevel === 'none') {
          //  remove user from usersWithAccess
          state.usersWithAccess = state.usersWithAccess.filter((user) => user.user.id !== userId);
        } else {
          // add user to usersWithAccess
          const updatedUserPermission = action.payload?.permission_level === 200 ? 'write' : 'read';
          const updatedUserWithPermission = { user: action.payload.user, permission_level: updatedUserPermission };
          const usersWithAccessWithUpdatedUser = state.usersWithAccess.find((user) => user.user.id === updatedUserWithPermission.user.id);

          state.usersWithAccess = usersWithAccessWithUpdatedUser
            ? state.usersWithAccess.map((user) => (user.user.id === updatedUserWithPermission.user.id ? updatedUserWithPermission : user))
            : [updatedUserWithPermission, ...state.usersWithAccess];
        }

        state.isUpdating = false;
        state.errorMessage = null;
      })
      .addCase(addUserFilePermission.pending, (state) => {
        state.isUpdating = true;
        state.errorMessage = null;
      })
      .addCase(addUserFilePermission.rejected, (state, action) => {
        state.isUpdating = false;
        state.errorMessage = action.payload;
      })
      .addCase(addCandidateFilePermission.fulfilled, (state, action) => {
        const { permissionLevel, email } = action.meta.arg;

        if (permissionLevel === 'none') {
          //  remove user from candidates
          state.candidates = state.candidates.filter((user) => user.email !== email);
        } else {
          // add user with scribe account to users with access as individually shared user
          const isUserWithScribeAccountAddedAsCandidate = action?.payload?.user;

          if (isUserWithScribeAccountAddedAsCandidate) {
            state.usersWithAccess = [
              { user: action.payload.user, permission_level: action.payload?.permission_level === 200 ? 'write' : 'read' },
              ...state.usersWithAccess
            ];
          } else {
            // add user to candidates
            const updatedCandidatePermission = action.payload;
            const candidatesWithExistingCandidate = state.candidates.find((user) => user.email === updatedCandidatePermission.email);

            state.candidates = candidatesWithExistingCandidate
              ? state.candidates.map(
                  (user) => (user.email === updatedCandidatePermission.email ? updatedCandidatePermission : user) // update existing candidate
                )
              : [updatedCandidatePermission, ...state.candidates]; // add new candidate
          }
        }
        state.isUpdating = false;
        state.errorMessage = null;
      })
      .addCase(addCandidateFilePermission.pending, (state) => {
        state.isUpdating = true;
        state.isUpdateError = false;
        state.errorMessage = null;
      })
      .addCase(addCandidateFilePermission.rejected, (state, action) => {
        state.isUpdating = false;
        state.isUpdateError = true;
        state.errorMessage = action.payload;
      })
      .addCase('scribe/updateFilePermission/pending', (state, action) => {
        state.isUpdating = true;
        state.errorMessage = null;
      })
      .addCase('scribe/updateFilePermission/rejected', (state, action) => {
        state.isUpdating = false;
        state.errorMessage = action.payload;
      })
      .addCase('scribe/updateFilePermission/fulfilled', (state, action) => {
        state.isUpdating = false;
        state.errorMessage = null;
      })
      .addCase(removeApplicationTag.fulfilled, (state, action) => {
        const { appTagId, type } = action.meta.arg;

        if (type === FileKindEnum.KnowledgePage) {
          state.currentKnowledgePage.app_tags = state.currentKnowledgePage.app_tags.filter((tag) => tag.id !== appTagId);
        }
        state.isUpdating = false;
        state.errorMessage = null;
      });
  }
});

export const selectKnowledgePage = createSelector(
  [
    (state) => state.knowledgePage.currentKnowledgePage,
    (state) => state.knowledgePage.isEditMode,
    (state) => state.knowledgePage.isError,
    (state) => state.knowledgePage.isLoading,
    (state) => state.knowledgePage.isUpdating,
    (state) => state.knowledgePage.documentViewerId !== undefined,
    (state) => state.knowledgePage.documentViewerId,
    (state) => state.knowledgePage.showingLoadingModal,
    (state) => state.knowledgePage.disableWindowFocus
  ],
  /**
   * @returns {KnowledgePageType}
   */
  (
    currentKnowledgePage,
    isEditMode,
    isError,
    isLoading,
    isUpdating,
    isDocumentViewerOpen,
    documentViewerId,
    showingLoadingModal,
    disableWindowFocus
  ) => ({
    currentKnowledgePage,
    isEditMode,
    isError,
    isLoading,
    isUpdating,
    isDocumentViewerOpen,
    documentViewerId,
    showingLoadingModal,
    disableWindowFocus
  })
);

export const { disableWindowFocus, setCurrentKnowledgePage, setDocumentViewerId, setLoadingModalActive, setLoadingModalDisabled } =
  knowledgePageSlice.actions;

export default knowledgePageSlice.reducer;
